import { useEffect, useRef, useState } from "react";
import {
  Briefcase,
  CheckCircle2,
  Clock,
  ExternalLink,
  MapPin,
  Plus,
  Send,
  Sparkles,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n";

interface TaskDispatchTabProps {
  idToken: string | null;
  isManager: boolean;
  employees?: Array<{ displayName: string; department?: string | null }>;
}

export function TaskDispatchTab({
  idToken,
  isManager,
  employees = [],
}: TaskDispatchTabProps) {
  const { language } = useLanguage();
  // The admin application is Thai-only. Ignore an employee language value
  // that may remain in localStorage when the same device opens /admin.
  const isMy = !isManager && language === "my";
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedToName, setAssignedToName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isOT, setIsOT] = useState(false);
  const [otHours, setOtHours] = useState("");
  const autoEndPreview = isOT && startTime && Number(otHours) > 0
    ? new Date(new Date(startTime).getTime() + (8 + Number(otHours)) * 60 * 60 * 1000).toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" })
    : "";

  const tasksQuery = trpc.task.list.useQuery(
    { idToken: idToken || "" },
    { enabled: Boolean(idToken) }
  );

  const createTask = trpc.task.create.useMutation({
    onSuccess: (data) => {
      toast.success(
        data.notificationSent
          ? "มอบหมายงานสำเร็จ พร้อมส่งแจ้งเตือน LINE ให้พนักงานแล้ว!"
          : "มอบหมายงานสำเร็จเรียบร้อย!"
      );
      setShowCreateModal(false);
      setTitle("");
      setDescription("");
      setLocationName("");
      setStartTime("");
      setEndTime("");
      setIsOT(false);
      setOtHours("");
      tasksQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถมอบหมายงานได้");
    },
  });

  const updateStatus = trpc.task.updateStatus.useMutation({
    onSuccess: (data, variables) => {
      const msg =
        variables.status === "accepted"
          ? "กดรับงานเรียบร้อยแล้ว!"
          : "ส่งงานเสร็จสิ้นเรียบร้อยแล้ว!";
      toast.success(msg);
      tasksQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถอัปเดตสถานะงานได้");
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken) return;
    if (!title.trim() || !assignedToName || !locationName.trim() || !startTime) {
      toast.error("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    createTask.mutate({
      idToken,
      title: title.trim(),
      description: description.trim() || undefined,
      assignedToName,
      locationName: locationName.trim(),
      startTime,
      endTime: endTime.trim() || undefined,
      isOT,
      otHours: isOT && otHours ? Number(otHours) : undefined,
    });
  };

  const tasks = tasksQuery.data?.tasks || [];
  const roster = isManager ? (tasksQuery.data?.roster || employees) : employees;
  const handledLineAction = useRef(false);

  useEffect(() => {
    if (handledLineAction.current || isManager || !idToken || tasks.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const taskId = Number(params.get("taskId"));
    if (params.get("action") !== "accept" || !Number.isInteger(taskId)) return;
    const task = tasks.find((item: any) => item.id === taskId);
    handledLineAction.current = true;
    if (task?.status === "assigned") {
      updateStatus.mutate({ idToken, taskId, status: "accepted" });
    }
  }, [idToken, isManager, tasks, updateStatus]);

  return (
    <div className="space-y-5">
      {/* Header Card */}
      <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
        <CardHeader className="p-5 sm:p-6 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-teal-600 to-emerald-500 text-white shadow-sm">
                <Briefcase size={18} />
              </div>
              <div>
                <CardTitle className="text-base font-black text-slate-900">
                  {isMy ? "တာဝန်ပေးအပ်ခြင်းနှင့် အချိန်ပို OT (Task & OT)" : "ระบบสั่งงานและงานล่วงเวลา (Task & OT Dispatch)"}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  {isManager
                    ? isMy
                      ? "တာဝန်၊ နေရာ၊ အချိန် သတ်မှတ်ပြီး LINE မှတစ်ဆင့် အကြောင်းကြားပါ"
                      : "กำหนดสั่งงาน เวลา สถานที่ รายบุคคล พร้อมแจ้งเตือนเข้า LINE"
                    : isMy
                    ? "မိမိတာဝန်များကို စစ်ဆေးပါ၊ အလုပ်စတင်ရန် 'လက်ခံမည်' နှင့် ပြီးဆုံးပါက 'အလုပ်အပ်မည်' ကို နှိပ်ပါ"
                    : "รายการคำสั่งงานที่ได้รับมอบหมาย รับงาน และส่งงาน"}
                </CardDescription>
              </div>
            </div>

            {isManager && (
              <Button
                size="sm"
                onClick={() => setShowCreateModal(!showCreateModal)}
                className="rounded-xl font-bold text-xs bg-teal-600 hover:bg-teal-700 text-white shadow-sm"
              >
                <Plus size={15} className="mr-1" />
                สั่งงานใหม่
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Create Task Form (Manager only) */}
      {isManager && showCreateModal && (
        <Card className="glass-panel rounded-3xl border-teal-200/80 bg-teal-50/30 shadow-md">
          <CardHeader className="p-5 sm:p-6 pb-2">
            <CardTitle className="text-sm font-black text-teal-950">
              📋 ฟอร์มกำหนดและสั่งงานใหม่
            </CardTitle>
            <CardDescription className="text-xs text-teal-800">
              ระบบจะส่ง LINE Flex Message แจ้งเตือนไปยังพนักงานทันที
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5 sm:p-6 pt-2">
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-black text-slate-700 mb-1">
                    ชื่องาน / รายละเอียดคำสั่งงาน *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น งานเทปูนชั้น 3 / ผูกเหล็กคาน / ก่ออิฐฉาบปูน"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-slate-700 mb-1">
                    ผู้รับมอบหมายงาน (รายบุคคล) *
                  </label>
                  <select
                    required
                    value={assignedToName}
                    onChange={(e) => setAssignedToName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-teal-500"
                  >
                    <option value="">-- เลือกพนักงาน --</option>
                    {roster.map((emp: { displayName: string; department?: string | null }) => (
                      <option key={emp.displayName} value={emp.displayName}>
                        {emp.displayName} {emp.department ? `(${emp.department})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-black text-slate-700 mb-1">
                    สถานที่ปฏิบัติงาน / ไซต์งานก่อสร้าง *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น ไซต์งานก่อสร้างโครงการ A / หน้างานอาคาร 2"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-slate-700 mb-1">
                    เวลาเริ่มงาน *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-700 mb-1">
                  รายละเอียดเพิ่มเติม (ถ้ามี)
                </label>
                <textarea
                  rows={2}
                  placeholder="ระบุคำแนะนำหรือสิ่งที่ต้องเตรียมพร้อม..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-teal-500"
                />
              </div>

              {/* OT Settings */}
              <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-amber-600" />
                    <span className="text-xs font-black text-amber-950">
                      งานนี้เป็นงานล่วงเวลา (OT) หรือไม่?
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={isOT}
                    onChange={(e) => setIsOT(e.target.checked)}
                    className="h-4 w-4 rounded text-amber-600 focus:ring-amber-500"
                  />
                </div>

                {isOT && (
                  <div className="pt-2 space-y-2">
                    <div className="flex items-center gap-3">
                    <label className="text-xs font-black text-amber-900 shrink-0">
                      จำนวนชั่วโมง OT (ชั่วโมง):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="24"
                      placeholder="เช่น 2 หรือ 3.5"
                      value={otHours}
                      onChange={(e) => setOtHours(e.target.value)}
                      className="w-32 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-bold text-amber-900 outline-none"
                    />
                    </div>
                    {autoEndPreview && <p className="rounded-xl bg-white px-3 py-2 text-xs font-bold text-amber-900">เวลาจบอัตโนมัติ: {autoEndPreview} (เวลางาน 8 ชม. + OT {Number(otHours).toLocaleString("th-TH")} ชม.)</p>}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-xl text-xs font-bold"
                >
                  ยกเลิก
                </Button>
                <Button
                  type="submit"
                  disabled={createTask.isPending}
                  size="sm"
                  className="rounded-xl text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white shadow-sm"
                >
                  <Send size={13} className="mr-1.5" />
                  {createTask.isPending ? "กำลังบันทึกและส่ง..." : "บันทึกและส่งคำสั่งงาน"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Task List */}
      <div className="space-y-3">
        <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
          <span>📋</span> {isMy ? `တာဝန်များ စာရင်း (${tasks.length})` : `รายการคำสั่งงาน (${tasks.length})`}
        </h3>

        {tasks.length === 0 ? (
          <div className="p-8 text-center rounded-3xl border border-dashed border-slate-200 bg-white/50">
            <p className="text-sm font-bold text-slate-400">
              {isMy ? "တာဝန်မှတ်တမ်း မရှိသေးပါ" : "ยังไม่มีรายการคำสั่งงาน"}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {tasks.map((task: any) => {
              const ot = Boolean(task.isOT);
              return (
                <div
                  key={task.id}
                  className={`p-4 rounded-2xl border transition-all ${
                    ot ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase ${
                            ot
                              ? "bg-amber-500 text-white"
                              : "bg-teal-600 text-white"
                          }`}
                        >
                          {ot
                            ? isMy
                              ? `⚡ အချိန်ပို OT (${task.otHours || 0} နာရီ)`
                              : `⚡ งาน OT (${Number.isFinite(Number(task.otHours)) ? Number(task.otHours).toLocaleString("th-TH", { maximumFractionDigits: 2 }) : "0"} ชม.)`
                            : isMy
                            ? "ပုံမှန်အလုပ်"
                            : "งานปกติ"}
                        </span>
                        <Badge
                          variant={
                            task.status === "completed"
                              ? "default"
                              : task.status === "accepted"
                              ? "secondary"
                              : "outline"
                          }
                          className="text-[10px] font-bold"
                        >
                          {task.status === "completed"
                            ? isMy
                              ? "✅ အလုပ်အပ်ပြီး"
                              : "✅ ส่งงานแล้ว"
                            : task.status === "accepted"
                            ? isMy
                              ? "⏳ လုပ်ဆောင်နေဆဲ"
                              : "⏳ กำลังดำเนินการ"
                            : isMy
                            ? "📋 တာဝန်ပေးထားသည်"
                            : "📋 มอบหมายแล้ว"}
                        </Badge>
                      </div>
                      <h4 className="text-sm font-black text-slate-900">{task.title}</h4>
                      {task.description && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 space-y-1 text-xs font-semibold text-slate-600 border-t border-slate-100 pt-2">
                    <p className="flex items-center gap-1.5">
                      <span className="text-slate-400">{isMy ? "👤 ဝန်ထမ်း:" : "👤 ผู้ปฏิบัติงาน:"}</span>
                      <strong className="text-slate-800">{task.assignedToName}</strong>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <MapPin size={13} className="text-teal-600 shrink-0" />
                      <span>{task.locationName}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Clock size={13} className="text-slate-400 shrink-0" />
                      <span>{(() => {
                        try {
                          const d = new Date(task.startTime);
                          return isNaN(d.getTime()) ? task.startTime : d.toLocaleString(isMy ? "my-MM" : "th-TH", { dateStyle: "medium", timeStyle: "short" });
                        } catch {
                          return task.startTime;
                        }
                      })()}</span>
                    </p>
                  </div>

                  {/* Employee Action Buttons */}
                  {!isManager && task.status !== "completed" && (
                    <div className="mt-3 flex gap-2 pt-2 border-t border-slate-100">
                      {task.status === "assigned" && (
                        <Button
                          size="sm"
                          onClick={() =>
                            idToken &&
                            updateStatus.mutate({ idToken, taskId: task.id, status: "accepted" })
                          }
                          disabled={updateStatus.isPending}
                          className="w-full rounded-xl text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          <CheckCircle2 size={14} className="mr-1" />
                          {isMy ? "✅ တာဝန်လက်ခံမည်" : "กดรับงาน"}
                        </Button>
                      )}
                      {task.status === "accepted" && (
                        <Button
                          size="sm"
                          onClick={() =>
                            idToken &&
                            updateStatus.mutate({ idToken, taskId: task.id, status: "completed" })
                          }
                          disabled={updateStatus.isPending}
                          className="w-full rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Send size={14} className="mr-1" />
                          {isMy ? "🚀 အလုပ်အပ်မည်" : "ส่งงานเสร็จสิ้น"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
